import {
  ApplicationConfig,
  APP_INITIALIZER,
  ErrorHandler,
  inject,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideClientHydration,
  withEventReplay,
  withNoIncrementalHydration,
} from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { routes } from './app.routes';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideTransloco } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { d2dTheme } from '@core/theme/d2d-theme';
import { authTokenInterceptor } from '@core/interceptors/auth-token.interceptor';
import { authErrorInterceptor } from '@core/interceptors/auth-error.interceptor';
import { initializeNavigationLoader } from '@core/interceptors/navigation-loader.interceptor';
import { ChunkLoadErrorHandler } from '@core/handlers/chunk-load-error.handler';
import { provideStateAdapters } from '@core/state/persistence';
import { ManifestNavigationService } from '@core/services/manifest/manifest-navigation.service';
import { TranslocoHttpLoader } from '@core/i18n/transloco-loader';
import { initializeLanguagePreference } from '@core/i18n/language-preference';

const initializeManifestNavigation = () => inject(ManifestNavigationService).initialize();

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
    MessageService,
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: d2dTheme,
        options: { darkModeSelector: '.app-dark' },
      },
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
    ),
    provideClientHydration(withEventReplay(), withNoIncrementalHydration()),
    provideHttpClient(withFetch(), withInterceptors([authTokenInterceptor, authErrorInterceptor])),
    provideStateAdapters(),
    provideTransloco({
      config: {
        availableLangs: ['es', 'en'],
        defaultLang: 'es',
        fallbackLang: 'es',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          useFallbackTranslation: true,
        },
      },
      loader: TranslocoHttpLoader,
    }),
    {
      provide: APP_INITIALIZER,
      useValue: initializeLanguagePreference,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useValue: initializeManifestNavigation,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useValue: initializeNavigationLoader,
      multi: true,
    },
  ],
};
